import {
  ServiceProvider,
  ServiceProviderProfileImage,
  ServiceProviderProfileImageVariantName,
} from '../types/firestore';

type ServiceProviderImageSource = Pick<ServiceProvider, 'photos' | 'isTestData'> & {
  profileImage?: ServiceProviderProfileImage | null;
};

const hasOwnProperty = Object.prototype.hasOwnProperty;

const trimToNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

interface ServiceProviderImageRecoveryContext {
  component?: string;
  providerId?: string | null;
  variant?: ServiceProviderProfileImageVariantName;
  reason?: 'missing-url' | 'broken-url';
  originalUrl?: string | null;
}

export const resolveServiceProviderImageUrl = (
  source: ServiceProviderImageSource | null | undefined,
  variant: ServiceProviderProfileImageVariantName = 'full'
): string | null => {
  if (!source) return null;
  if (source.isTestData === true) return null;

  if (hasOwnProperty.call(source, 'profileImage')) {
    const profileImage = source.profileImage;
    if (!profileImage) {
      return null;
    }

    const candidateUrl = profileImage[variant]?.url;
    return trimToNonEmptyString(candidateUrl);
  }

  if (!Array.isArray(source.photos)) {
    return null;
  }

  return trimToNonEmptyString(source.photos[0]);
};

export const resolveServiceProviderImageStoragePath = (
  source: ServiceProviderImageSource | null | undefined,
  variant: ServiceProviderProfileImageVariantName = 'full'
): string | null => {
  if (!source) return null;
  if (source.isTestData === true) return null;
  if (!hasOwnProperty.call(source, 'profileImage')) return null;
  const profileImage = source.profileImage;
  if (!profileImage) return null;
  return trimToNonEmptyString(profileImage[variant]?.storagePath);
};

export const logServiceProviderImageRenderIssue = (
  context: ServiceProviderImageRecoveryContext & { storagePath?: string | null; error?: unknown }
): void => {
  if (typeof console === 'undefined' || typeof console.warn !== 'function') return;
  console.warn('[serviceProviderImage] render fallback', {
    component: context.component || 'unknown',
    providerId: context.providerId || null,
    variant: context.variant || 'full',
    reason: context.reason || 'broken-url',
    originalUrl: context.originalUrl || null,
    storagePath: context.storagePath || null,
    errorCode:
      context.error && typeof context.error === 'object'
        ? String((context.error as { code?: unknown }).code || '')
        : '',
    errorMessage:
      context.error && typeof context.error === 'object'
        ? String((context.error as { message?: unknown }).message || context.error)
        : String(context.error || ''),
  });
};

export const recoverServiceProviderImageDownloadUrl = async (
  storagePath: string | null | undefined,
  context: ServiceProviderImageRecoveryContext = {}
): Promise<string | null> => {
  const normalizedStoragePath = trimToNonEmptyString(storagePath);
  if (!normalizedStoragePath) {
    logServiceProviderImageRenderIssue({
      ...context,
      storagePath: null,
      error: 'missing-storage-path',
    });
    return null;
  }

  try {
    const [{ getDownloadURL, ref }, { storage }] = await Promise.all([
      import('firebase/storage'),
      import('../firebase'),
    ]);
    const recoveredUrl = await getDownloadURL(ref(storage, normalizedStoragePath));
    const normalizedRecoveredUrl = trimToNonEmptyString(recoveredUrl);
    if (!normalizedRecoveredUrl) {
      logServiceProviderImageRenderIssue({
        ...context,
        storagePath: normalizedStoragePath,
        error: 'empty-recovered-url',
      });
      return null;
    }

    return normalizedRecoveredUrl;
  } catch (error) {
    logServiceProviderImageRenderIssue({
      ...context,
      storagePath: normalizedStoragePath,
      error,
    });
    return null;
  }
};
